import { useState } from "react";
import { money } from "../../lib/format";

export interface FlowPoint { day: string; deposits: number; withdrawals: number }

const WIDTH = 720;
const HEIGHT = 190;
const TOP = 14;
const BOTTOM = 26;
const LEFT = 6;
const RIGHT = 6;

const dayLabel = (iso: string) => {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year!, month! - 1, day!).toLocaleDateString("es-MX", { day: "numeric", month: "short" });
};

/**
 * Settled deposits and withdrawals per day as paired thin bars, with a hover tooltip per day. Both
 * series share one axis (same unit), the legend names them and each is coloured by a fixed slot;
 * colours are theme tokens (`--chart-1`, `--chart-2`) validated for light and dark surfaces.
 */
export function FlowChart({ points, label }: { points: FlowPoint[]; label: string }) {
  const [active, setActive] = useState<number | null>(null);
  if (!points.length) return null;
  const max = Math.max(1, ...points.flatMap((point) => [point.deposits, point.withdrawals]));
  const plotHeight = HEIGHT - TOP - BOTTOM;
  const slot = (WIDTH - LEFT - RIGHT) / points.length;
  const gap = 2;
  const barWidth = Math.max(4, Math.min(14, (slot - 10) / 2));
  const baseline = HEIGHT - BOTTOM;
  const yFor = (value: number) => baseline - (value / max) * plotHeight;
  const empty = points.every((point) => point.deposits === 0 && point.withdrawals === 0);
  const current = active === null ? null : points[active];

  return (
    <div className="flow-chart" onMouseLeave={() => setActive(null)}>
      <div className="flow-legend" aria-hidden="true">
        <span><i className="swatch one" /> Depósitos</span>
        <span><i className="swatch two" /> Retiros</span>
      </div>
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} preserveAspectRatio="none" role="img" aria-label={label}>
        {[0.5, 1].map((ratio) => (
          <line key={ratio} x1={LEFT} x2={WIDTH - RIGHT} y1={yFor(max * ratio)} y2={yFor(max * ratio)} className="grid" />
        ))}
        <line x1={LEFT} x2={WIDTH - RIGHT} y1={baseline} y2={baseline} className="axis" />
        {points.map((point, index) => {
          const center = LEFT + slot * index + slot / 2;
          const depositsY = yFor(point.deposits);
          const withdrawalsY = yFor(point.withdrawals);
          return (
            <g key={point.day} className={active === index ? "day active" : "day"} onMouseEnter={() => setActive(index)} onFocus={() => setActive(index)} tabIndex={0} aria-label={`${dayLabel(point.day)}: depósitos ${money.format(point.deposits)}, retiros ${money.format(point.withdrawals)}`}>
              <rect x={LEFT + slot * index} y={TOP} width={slot} height={HEIGHT - TOP - BOTTOM} className="hit" />
              {point.deposits > 0 && <rect x={center - barWidth - gap / 2} y={depositsY} width={barWidth} height={baseline - depositsY} rx={3} className="bar one" />}
              {point.withdrawals > 0 && <rect x={center + gap / 2} y={withdrawalsY} width={barWidth} height={baseline - withdrawalsY} rx={3} className="bar two" />}
              {(index % 2 === points.length % 2 || points.length <= 7) && (
                <text x={center} y={HEIGHT - 8} textAnchor="middle" className="tick">{dayLabel(point.day)}</text>
              )}
            </g>
          );
        })}
        {empty && <text x={WIDTH / 2} y={HEIGHT / 2} textAnchor="middle" className="empty">Sin movimientos confirmados en estos 14 días</text>}
      </svg>
      <div className="flow-tooltip" role="status" aria-live="polite">
        {current ? (
          <>
            <strong>{dayLabel(current.day)}</strong>
            <span><i className="swatch one" /> Depósitos <b translate="no">{money.format(current.deposits)}</b></span>
            <span><i className="swatch two" /> Retiros <b translate="no">{money.format(current.withdrawals)}</b></span>
          </>
        ) : <span className="flow-tooltip-hint">Pasa el cursor por un día para ver los montos.</span>}
      </div>
    </div>
  );
}
